import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import useAuth from '@/hooks/useAuth'
import { loadDocsNav } from './docsNav'
import { DocsNavContext } from './docsNavContext'

const EMPTY_NAV = { categories: [], unavailable: [] }

/**
 * One nav fetch per docs screen, shared by everything that needs it.
 *
 * Five components want the tree: the sidebar, the Categories menu, the
 * breadcrumbs, the landing page and the category index. Calling the
 * loader in each is *correct* — the manifest cache dedupes the actual
 * work — but each copy would keep its own `loading` state and settle
 * independently, so one page mount would produce four separate
 * re-render waves under the reader's cursor.
 *
 * Mounted once in `DocsLayout`, above both the sidebar and the content
 * slot, so a single settle serves the whole screen.
 */
export function DocsNavProvider({ children }) {
    const { user } = useAuth()
    const [nav, setNav] = useState(null)
    const [loading, setLoading] = useState(true)

    // Depend on the roles, not the user object: `useAuth` hands back a
    // fresh identity on unrelated state changes, and an object dependency
    // would refetch on each one. The roles are what the nav is filtered
    // by, so they are what it should react to.
    const rolesKey = (user?.roles ?? []).join(',')

    useEffect(() => {
        let cancelled = false
        setLoading(true)

        loadDocsNav(user)
            .then((loaded) => {
                if (!cancelled) setNav(loaded)
            })
            .catch(() => {
                // `loadDocsNav` folds a failed audience into `unavailable`
                // rather than rejecting, so reaching here means something
                // unexpected. Settle on an empty nav so the surfaces show
                // their own empty state rather than spinning forever.
                if (!cancelled) setNav(EMPTY_NAV)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rolesKey])

    // Memoised on the parts, not the object, so consumers re-render when
    // the nav actually changes rather than on every parent render.
    const value = useMemo(() => ({ nav, loading }), [nav, loading])

    return (
        <DocsNavContext.Provider value={value}>
            {children}
        </DocsNavContext.Provider>
    )
}

DocsNavProvider.propTypes = {
    children: PropTypes.node
}
